import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { quotaAPI } from "@/lib/api";
import { Flame, Calendar } from "lucide-react";

const TXT = {
    fr: {
        title: "Historique de production",
        subtitle: "30 derniers jours · continue ta série 🔥",
        streak_one: "jour d'affilée",
        streak_many: "jours d'affilée",
        total: "Shorts produits",
        empty: "Aucun Short encore — lance ta série aujourd'hui.",
        today: "Aujourd'hui",
    },
    en: {
        title: "Production history",
        subtitle: "Last 30 days · keep the streak going 🔥",
        streak_one: "day streak",
        streak_many: "day streak",
        total: "Shorts produced",
        empty: "No Short yet — start your streak today.",
        today: "Today",
    },
    es: {
        title: "Historial de producción",
        subtitle: "Últimos 30 días · mantén la racha 🔥",
        streak_one: "día de racha",
        streak_many: "días de racha",
        total: "Shorts producidos",
        empty: "Aún ningún Short — empieza tu racha hoy.",
        today: "Hoy",
    },
};

export default function HistoryGraph() {
    const { lang, quota } = useApp();
    const t = TXT[lang] || TXT.fr;
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const d = await quotaAPI.history(30);
                if (!cancelled) setData(d);
            } catch {
                if (!cancelled) setData(null);
            }
        };
        load();
        const onChange = () => load();
        window.addEventListener("sf:quota-changed", onChange);
        return () => { cancelled = true; window.removeEventListener("sf:quota-changed", onChange); };
    }, [quota?.used]);

    if (!data) return null;

    const max = Math.max(1, ...data.series.map((d) => d.count));
    const today = data.series[data.series.length - 1]?.date;
    const streakLabel = data.streak === 1 ? t.streak_one : t.streak_many;

    return (
        <div data-testid="history-graph" className="mb-6 rounded-lg border border-[#253046] bg-[#0F141C] p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg grid place-items-center bg-[#FF4D00]/10 text-[#FF4D00]">
                        <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="font-display text-sm sm:text-base font-bold uppercase leading-tight">{t.title}</div>
                        <div className="text-[11px] font-mono-sf text-slate-500 tracking-wider">{t.subtitle}</div>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div data-testid="history-streak" className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-[#FF4D00]/40 bg-[#FF4D00]/10">
                        <Flame className="w-3.5 h-3.5 text-[#FF4D00]" fill="currentColor" />
                        <span className="font-display text-sm font-bold text-white">{data.streak}</span>
                        <span className="text-[10px] font-mono-sf text-slate-400 uppercase tracking-widest">{streakLabel}</span>
                    </div>
                    <div className="text-right">
                        <div data-testid="history-total" className="font-display text-lg font-black flame-text leading-none">{data.total}</div>
                        <div className="text-[10px] font-mono-sf text-slate-500 uppercase tracking-widest">{t.total}</div>
                    </div>
                </div>
            </div>

            {data.total === 0 ? (
                <div className="text-xs text-slate-500 italic py-6 text-center">{t.empty}</div>
            ) : null}

            <div className="flex items-end justify-between gap-[3px] h-16 sm:h-20">
                {data.series.map((d) => {
                    const h = d.count === 0 ? 6 : Math.max(10, Math.round((d.count / max) * 100));
                    const isToday = d.date === today;
                    const color = d.count === 0 ? "bg-[#161D2A]" : isToday ? "bg-gradient-to-t from-[#FF4D00] to-[#FFB800]" : "bg-[#FF4D00]/70 hover:bg-[#FF4D00]";
                    return (
                        <div
                            key={d.date}
                            data-testid={`history-bar-${d.date}`}
                            title={`${d.date} · ${d.count} Shorts`}
                            style={{ height: `${h}%` }}
                            className={`flex-1 min-w-[4px] rounded-t ${color} kinetic`}
                        />
                    );
                })}
            </div>

            <div className="mt-2 flex justify-between text-[10px] font-mono-sf text-slate-600 tracking-widest">
                <span>−30j</span>
                <span className="text-[#FF4D00]">{t.today}</span>
            </div>
        </div>
    );
}
